"use client";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { Veiculo } from "@/types/Veiculo";

import { EmptyField } from "@/components/ui/empty-field";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Plus } from "lucide-react";
import { useState } from "react";
import VehicleForm from "./vehicle-form";

interface TabVeiculosProps {
  itens?: Veiculo[];
}

export default function TabVeiculos({ itens }: TabVeiculosProps) {
  const { user } = useAuth();
  const cliCodigo = user?.cli_codigo ?? "";

  const [veiculos, setVeiculos] = useState<Veiculo[]>(itens ?? []);
  const [adding, setAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleUpdate = (updated: Veiculo) => {
    setVeiculos((prev) =>
      prev.map((v) => (v.cve_chassi === updated.cve_chassi ? updated : v))
    );
    setEditingIndex(null);
  };

  const handleAdd = (added: Veiculo) => {
    setVeiculos((prev) => [...prev, added]);
    setAdding(false);
  };

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-n-primary">
          Veículos cadastrados:
        </h2>
        {!adding && editingIndex === null && (
          <Button
            type="button"
            variant="border"
            size="sm"
            onClick={() => setAdding(true)}
          >
            Adicionar veículo <Plus size={14} />
          </Button>
        )}
      </div>

      <Separator />

      {adding && (
        <VehicleForm
          cliCodigo={cliCodigo}
          onSaved={handleAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      {veiculos.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">🚗</div>
          <p className="text-gray-500 text-sm">Nenhum veículo cadastrado</p>
          <p className="text-gray-400 text-xs">
            Adicione seu primeiro veículo para começar
          </p>
        </div>
      ) : (
        <ul role="list" className="w-full space-y-6">
          {veiculos.map((v, i) =>
            editingIndex === i ? (
              <li key={v.cve_chassi ?? i}>
                <VehicleForm
                  cliCodigo={cliCodigo}
                  initial={v}
                  onSaved={handleUpdate}
                  onCancel={() => setEditingIndex(null)}
                />
              </li>
            ) : (
              <li key={v.cve_chassi ?? i} className="space-y-2">
                <span className="font-semibold text-n-text-primary text-sm">
                  {v.vmd_descricao || (
                    <EmptyField>Marca e modelo não informados</EmptyField>
                  )}
                </span>
                <div className="flex gap-4 justify-between">
                  <div className="flex flex-col md:flex-row w-[85%] justify-between text-sm gap-4">
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        Placa:
                      </span>
                      <span className="text-n-text-neutral">
                        {v.cve_placa || (
                          <EmptyField>Placa não informada</EmptyField>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        Ano/modelo:
                      </span>
                      <span className="text-n-text-neutral">
                        {v.cve_ano_fab && v.cve_ano_mod ? (
                          `${v.cve_ano_fab}/${v.cve_ano_mod}`
                        ) : (
                          <EmptyField>Ano não informado</EmptyField>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        Cor:
                      </span>
                      <span className="text-n-text-neutral">
                        {v.cor_descricao || (
                          <EmptyField>Cor não informada</EmptyField>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-n-text-primary">
                        Chassi:
                      </span>
                      <span className="text-n-text-neutral">
                        {v.cve_chassi || (
                          <EmptyField>Chassi não informado</EmptyField>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-end items-start md:w-[15%] min-w-20">
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={() => setEditingIndex(i)}
                      className="text-sm"
                    >
                      Editar
                      <ArrowRight size={14} />
                    </Button>
                  </div>
                </div>
                {i < veiculos.length - 1 && <Separator />}
              </li>
            )
          )}
        </ul>
      )}
    </div>
  );
}
